import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";

const tabs = [
  {
    id: "philosophy",
    label: "Our Philosophy",
    heading: "Our Philosophy",
    intro: "We believe that providing our clients with efficient, accurate service in our practice areas is fundamental — and it is built on two things.",
    image: "https://images.pexels.com/photos/6077983/pexels-photo-6077983.jpeg",
    imageAlt: "Two people shaking hands over a laptop",
    points: [
      {
        title: "The Power of Partnership",
        body: "We are not simply a service provider, but a firm that works in partnership with you as a client, getting to understand what makes you tick.",
      },
      {
        title: "Added Insight",
        body: "We see our role as offering you choices about how you manage your business at any given time, based on our wide-ranging business and taxation knowledge.",
      },
    ],
  },
  {
    id: "mission",
    label: "Our Mission",
    heading: "Our Mission",
    intro: "To make our clients successful by merging brilliant service and execution with the insight gained from our business experience.",
    image: "https://images.pexels.com/photos/5918206/pexels-photo-5918206.jpeg",
    imageAlt: "Two colleagues reviewing a financial report together",
    points: [
      {
        title: "Brilliant Service",
        body: "We can only help our clients succeed by ensuring that they have their accounts and compliance in order and making that process as time efficient for our clients as possible.",
      },
      {
        title: "Business Insights",
        body: "We apply our extensive business experience to give practical advice as to how our clients should proceed to their best advantage.",
      },
    ],
  },
  {
    id: "vision",
    label: "Our Vision",
    heading: "Our Vision",
    intro: "To create a community of satisfied clients whose businesses are thriving, thereby supporting the lifestyles and aspirations of those who work there.",
    image: "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg",
    imageAlt: "A team collaborating in a modern office",
    points: [
      {
        title: "Clients First, Always",
        body: "Every piece of advice we give starts with one question — is this in our client's best interest? If the answer is yes, everything else follows.",
      },
      {
        title: "Growing Together",
        body: "As our clients' businesses thrive, so do the people behind them. That is the community we are building, one set of accounts at a time.",
      },
    ],
  },
];

export default function AboutUs() {
  const [active, setActive] = useState("philosophy");
  const tab = tabs.find((t) => t.id === active);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="about-page">
      <Nav />
      <main>
        <section className="pt-36 lg:pt-44" data-testid="about-header">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="overline-label mb-6 flex items-center gap-4"
              data-testid="about-page-overline"
            >
              <span className="inline-block h-px w-10 bg-gold" />
              About Us
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl"
              data-testid="about-page-heading"
            >
              Who we are, and what we <em className="italic text-gold">stand for.</em>
            </motion.h1>
          </div>
        </section>

        <section className="py-20 lg:py-24" data-testid="about-tabs-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="flex flex-wrap gap-3" role="tablist" aria-label="About us sections">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active === t.id}
                  data-testid={`about-tab-${t.id}`}
                  onClick={() => setActive(t.id)}
                  className={`border px-7 py-3.5 font-sans text-sm font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                    active === t.id
                      ? "border-navy bg-navy text-cream"
                      : "border-navy/25 text-navy/70 hover:border-gold hover:text-navy"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={tab.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-14 grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16"
                data-testid={`about-panel-${tab.id}`}
              >
                <div className="group relative">
                  <div className="absolute -left-4 -top-4 h-full w-full border hairline-gold transition-transform duration-500 group-hover:-translate-x-1 group-hover:-translate-y-1" aria-hidden="true" />
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={tab.image}
                      alt={tab.imageAlt}
                      className="h-full w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
                      data-testid={`about-image-${tab.id}`}
                    />
                    <div className="absolute inset-0 bg-navy/10 transition-opacity duration-700 group-hover:opacity-0" />
                  </div>
                </div>

                <div>
                  <h2 className="font-serif text-3xl font-medium tracking-tight text-navy sm:text-4xl" data-testid={`about-heading-${tab.id}`}>
                    {tab.heading}
                  </h2>
                  <p className="mt-6 max-w-lg text-base leading-relaxed text-muted sm:text-lg">
                    {tab.intro}
                  </p>
                  <div className="mt-10 space-y-8">
                    {tab.points.map((p, i) => (
                      <div key={p.title} className="flex items-start gap-4" data-testid={`about-point-${tab.id}-${i + 1}`}>
                        <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold">
                          <Check size={13} className="text-gold" strokeWidth={3} />
                        </span>
                        <div>
                          <h3 className="font-sans text-sm font-bold uppercase tracking-[0.16em] text-navy">
                            {p.title}
                          </h3>
                          <p className="mt-2 max-w-md text-base leading-relaxed text-muted">
                            {p.body}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
